import { type Locale, t } from "./i18n";
import type { ModuleId } from "./site";

type Zone = {
  className: string;
  index: string;
  modules: ModuleId[];
};

const zones: Zone[] = [
  { className: "guest", index: "01", modules: ["loyalty", "reservations"] },
  { className: "service", index: "02", modules: ["orders", "tables"] },
  { className: "counter", index: "03", modules: ["pos", "invoice"] },
  { className: "back", index: "04", modules: ["inventory", "supply"] },
];

export function HeroSystemMap({ locale }: { locale: Locale }) {
  return (
    <div className="system-map" role="img" aria-label={t(locale, "official.hero.artLabel")}>
      <svg className="restaurant-plan" viewBox="0 0 720 720" aria-hidden="true">
        <defs>
          <linearGradient id="floor" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#fffdf9" />
            <stop offset="1" stopColor="#e7dcd1" />
          </linearGradient>
          <linearGradient id="counter" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#6d4090" />
            <stop offset="1" stopColor="#3d2057" />
          </linearGradient>
          <filter id="soft-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="12" stdDeviation="16" floodColor="#2f1848" floodOpacity=".14" />
          </filter>
        </defs>

        <rect x="30" y="30" width="660" height="660" rx="58" fill="url(#floor)" />
        <path className="plan-grid" d="M56 190H664M56 512H664M245 56V664" />

        <g className="plan-counter" filter="url(#soft-shadow)">
          <path d="M72 88h138c18 0 28 10 28 28v142H72V88Z" fill="url(#counter)" />
          <rect x="96" y="119" width="58" height="84" rx="13" fill="#f8f2ea" />
          <rect x="105" y="130" width="40" height="48" rx="7" fill="#281637" />
          <path d="M111 143h28M111 152h21" stroke="#ef9a70" strokeWidth="4" strokeLinecap="round" />
          <circle cx="125" cy="190" r="5" fill="#ef9a70" />
          <path d="M177 119h33v68l-7-5-7 5-7-5-6 5-6-5V119Z" fill="#fffdf9" opacity=".9" />
          <path d="M184 135h18M184 145h18M184 155h12" stroke="#5a2f82" strokeWidth="3" strokeLinecap="round" opacity=".65" />
        </g>

        <g className="plan-entry">
          <path d="M586 58v86M662 58v86" stroke="#5a2f82" strokeWidth="5" strokeLinecap="round" />
          <path d="M588 142c37-2 67-30 72-68" fill="none" stroke="#5a2f82" strokeDasharray="5 7" strokeWidth="2" opacity=".5" />
          <circle cx="624" cy="112" r="15" fill="#ef9a70" />
          <circle className="pulse-ring" cx="624" cy="112" r="26" fill="none" stroke="#ef9a70" strokeWidth="3" />
        </g>

        <g className="dining-table dining-table--one" transform="translate(420 218)">
          <circle r="68" fill="#fffdf9" stroke="#c89c5b" strokeWidth="5" filter="url(#soft-shadow)" />
          <circle r="25" fill="#f4efe7" stroke="#5a2f82" strokeOpacity=".16" strokeWidth="3" />
          <path d="M-12-2h24M0-14v24" stroke="#ef9a70" strokeWidth="3" strokeLinecap="round" />
          <g fill="#5a2f82"><rect x="-24" y="-99" width="48" height="20" rx="10"/><rect x="-24" y="79" width="48" height="20" rx="10"/><rect x="79" y="-24" width="20" height="48" rx="10"/><rect x="-99" y="-24" width="20" height="48" rx="10"/></g>
        </g>

        <g className="dining-table dining-table--two" transform="translate(578 350)">
          <circle r="50" fill="#fffdf9" stroke="#c89c5b" strokeWidth="4" />
          <circle r="19" fill="#eee4f6" />
          <g fill="#5a2f82"><rect x="-20" y="-75" width="40" height="17" rx="9"/><rect x="-20" y="58" width="40" height="17" rx="9"/><rect x="58" y="-20" width="17" height="40" rx="9"/></g>
        </g>

        <g className="dining-table dining-table--three" transform="translate(387 447)">
          <circle r="47" fill="#fffdf9" stroke="#c89c5b" strokeWidth="4" />
          <circle r="17" fill="#f7e3d8" />
          <g fill="#5a2f82"><rect x="-19" y="-70" width="38" height="16" rx="8"/><rect x="-19" y="54" width="38" height="16" rx="8"/><rect x="54" y="-19" width="16" height="38" rx="8"/></g>
        </g>

        <g className="plan-kitchen">
          <rect x="64" y="535" width="154" height="122" rx="24" fill="#ded2c7" />
          <rect x="82" y="552" width="118" height="32" rx="10" fill="#fffdf9" />
          <g fill="none" stroke="#5a2f82" strokeWidth="4" opacity=".72">
            <circle cx="104" cy="568" r="8" /><circle cx="132" cy="568" r="8" /><circle cx="160" cy="568" r="8" /><circle cx="188" cy="568" r="8" />
          </g>
          <rect x="82" y="600" width="54" height="40" rx="10" fill="#5a2f82" opacity=".88" />
          <path d="M94 620h30" stroke="#ef9a70" strokeWidth="4" strokeLinecap="round" />
          <g fill="#c89c5b"><rect x="152" y="600" width="48" height="16" rx="5"/><rect x="152" y="624" width="48" height="16" rx="5"/></g>
        </g>

        <g className="plan-reservation" transform="translate(544 560) rotate(-4)">
          <rect width="98" height="74" rx="15" fill="#fffdf9" stroke="#5a2f82" strokeOpacity=".18" strokeWidth="2" />
          <path d="M18 25h61M18 39h42M18 53h52" stroke="#5a2f82" strokeWidth="4" strokeLinecap="round" opacity=".6" />
          <circle cx="81" cy="19" r="7" fill="#ef9a70" />
        </g>

        <path className="loyalty-route loyalty-route--base" d="M624 112C585 130 531 144 484 186S399 272 321 278 204 247 126 203" />
        <path className="loyalty-route loyalty-route--active" d="M624 112C585 130 531 144 484 186S399 272 321 278 204 247 126 203" />
        <circle className="route-node route-node--guest" cx="624" cy="112" r="8" />
        <circle className="route-node route-node--table" cx="450" cy="214" r="8" />
        <circle className="route-node route-node--counter" cx="126" cy="203" r="8" />

        <path className="service-route" d="M126 203c26 75 22 204 16 344M450 214c-25 116-73 200-167 314M578 350c-75 46-130 98-169 169" />
        <circle cx="142" cy="525" r="5" fill="#c89c5b" />
        <circle cx="283" cy="528" r="5" fill="#c89c5b" />
        <circle cx="409" cy="519" r="5" fill="#c89c5b" />
      </svg>

      <div className="system-map__topline">
        <span><i />{t(locale, "official.platform.first")}</span>
        <strong>{t(locale, "official.hero.artCore")}</strong>
      </div>

      {zones.map((zone) => (
        <div className={`system-zone system-zone--${zone.className}`} key={zone.index}>
          <span>{zone.index}</span>
          <div>
            {zone.modules.map((id) => (
              <strong className={id === "loyalty" ? "is-live" : ""} key={id}>
                {t(locale, `official.platform.${id}.name`)}
              </strong>
            ))}
          </div>
        </div>
      ))}

      <div className="system-map__legend">
        <span><i className="is-live" />{t(locale, "official.platform.first")}</span>
        <span><i />{t(locale, "official.platform.planned")}</span>
      </div>
    </div>
  );
}
